/**
 * heightFactor.js
 *
 * The landing sky's compact-layout factor, t.
 *
 * WHY THIS EXISTS
 * ---------------
 * Common small laptops hand the browser a short window — about 1204x640 on a
 * 13-inch MacBook, about 1366x650 on budget Windows laptops. The full-height
 * layout needs roughly 740px, so those visitors had to scroll, and on first view
 * the footer was below the fold and the mountains were cut off.
 *
 * t drives a compact layout at those heights: 0 at 640px tall and below, 1 at
 * 760px tall and above, linear in between. Every height-sensitive value in
 * LandingSky.css is written as "compact + t * (full - compact)", so t = 1
 * reproduces the current page exactly and t = 0 is the approved 1204x640
 * composition.
 *
 * WHY t IS COMPUTED HERE RATHER THAN IN CSS
 * -----------------------------------------
 * Four of the eight height-sensitive values are RATIOS of a width-based clamp()
 * (the headline, paragraph, CTA and gap shrink by a fixed percentage). Expressing
 * those in pure CSS needs a unitless number derived from the viewport height, and
 * there is no portable way to get one: dividing a length by a length is not valid
 * in calc(), so `100vh / 760px` is unavailable. The four absolute pairs could be
 * done with vh-based clamp() in the same idiom as the width tokens, but splitting
 * the mechanism across two techniques for one ruling is worse than computing one
 * number in JavaScript and letting CSS do the rest.
 *
 * So: this module produces t, LandingSky.js writes it to the page element as the
 * custom property --landing-sky-t, and LandingSky.css reads it. CSS still owns
 * every actual value.
 */

/** Viewport height at or below which the compact layout is held. */
export const COMPACT_HEIGHT = 640;

/** Viewport height at or above which the full-height layout is held. */
export const FULL_HEIGHT = 760;

/**
 * Width at or below which the compact layout is suppressed entirely (t = 1), so
 * the interim mobile fallback keeps its own values untouched.
 *
 * PAIRED WITH CSS: this must equal the `max-width` of the interim mobile blocks
 * in LandingSky.css. It cannot be enforced from CSS, because LandingSky.js writes
 * --landing-sky-t as an INLINE style, and an inline style beats any stylesheet
 * rule including a media query — so a `@media (max-width: 800px) { --landing-sky-t: 1 }`
 * would be silently ignored. The gate has to live on the JavaScript side.
 */
export const COMPACT_WIDTH_FLOOR = 800;

/**
 * t for a given viewport, clamped to [0, 1].
 *
 * @param {number} viewportWidth  window.innerWidth
 * @param {number} viewportHeight window.innerHeight
 * @returns {number} 0 = compact, 1 = full height
 */
export function computeHeightFactor(viewportWidth, viewportHeight) {
  // Narrow viewports are the interim mobile fallback's business. Returning 1
  // makes every "compact + t * (full - compact)" expression collapse to its
  // full-height value, after which the mobile blocks override what they need.
  if (viewportWidth <= COMPACT_WIDTH_FLOOR) return 1;

  const span = FULL_HEIGHT - COMPACT_HEIGHT;
  const raw = (viewportHeight - COMPACT_HEIGHT) / span;
  return Math.min(1, Math.max(0, raw));
}
